/**
 * VenombloodSkillEffect — Poison tree Expel path upgrade (expel_retaliate).
 *
 * Passive retaliation: whenever the player takes damage from any source,
 * the attacker receives a `poison` DoT in return. Melee attackers eat the
 * full 4-second dose; ranged attackers get a reduced 2-second application
 * (upgradeable to the full 4 seconds by the Thorns cross-path upgrade).
 *
 * This fires regardless of whether Expel is currently active — Venomblood
 * is a permanent passive response, not aura-dependent. The integration
 * layer calls `handlePlayerHit()` on every player-damage event.
 *
 * Intensity inherits the tree's base poison intensity (3), scaled by the
 * Toxicologist tree passive (`dotDamageMultiplier`). Duration is resolved
 * from the UpgradeEffectResolver:
 *   - meleeRetaliationDotMs: 4000 (from own node, enhancesId: 'expel')
 *   - rangedRetaliationDotMs: 2000 base, 4000 with Thorns
 *     (Thorns enhancesId: 'expel_retaliate' — cross-scope max)
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Venomblood (expel_retaliate)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface VenombloodDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      targetEntityId: string;
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface VenombloodConfig {
  /** Base poison intensity per tick (tree standard). */
  readonly baseIntensity: number;
}

const DEFAULT_CONFIG: VenombloodConfig = {
  baseIntensity: 3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class VenombloodSkillEffect implements SkillEffect {
  readonly skillId = 'expel_retaliate';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: VenombloodDeps;
  private readonly config: VenombloodConfig;

  private _retaliationsApplied = 0;

  constructor(deps: VenombloodDeps, config: Partial<VenombloodConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get retaliationsApplied(): number { return this._retaliationsApplied; }

  // --- External event handler ---

  /**
   * Called by the integration layer when the player absorbs a hit.
   * Resolves the DoT duration for the given attack type and poisons
   * the attacker in return.
   *
   * @param attackerEntityId — entity that struck the player
   * @param attackType — 'melee' or 'ranged'
   */
  handlePlayerHit(attackerEntityId: string, attackType: 'melee' | 'ranged'): void {
    const durationMs = this.resolveDurationMs(attackType);
    const intensity = this.resolveIntensity();

    this.deps.statusEffects.apply('poison', {
      targetEntityId: attackerEntityId,
      sourceSkillId: 'expel_retaliate',
      durationMs,
      intensity,
    });

    this._retaliationsApplied++;
  }

  // --- SkillEffect interface (passive — no activation logic) ---

  update(_delta: number): void { /* passive — no per-frame logic */ }

  destroy(): void { /* no resources to release */ }

  // --- Private helpers ---

  /**
   * Resolve retaliation DoT duration for the given attack type.
   *
   * Melee: resolver query on 'expel' scope (Venomblood's enhancesId)
   *   → meleeRetaliationDotMs = 4000
   *
   * Ranged: takes the max of two scopes:
   *   - 'expel' scope: base rangedRetaliationDotMs = 2000
   *   - 'expel_retaliate' scope: Thorns override = 4000 (when unlocked)
   *   This cross-scope max handles the Thorns cross-path enhancement.
   */
  private resolveDurationMs(attackType: 'melee' | 'ranged'): number {
    if (attackType === 'melee') {
      return this.deps.resolver.getAdditiveEffect('expel_retaliate', 'meleeRetaliationDotMs');
    }

    // Ranged: base from own node + possible Thorns override
    const base = this.deps.resolver.getAdditiveEffect('expel_retaliate', 'rangedRetaliationDotMs');
    const thornsOverride = this.deps.resolver.getAdditiveEffect('expel_retaliate', 'rangedRetaliationDotMs');
    return Math.max(base, thornsOverride);
  }

  /**
   * Resolve poison intensity: base (3) scaled by Toxicologist tree passive.
   */
  private resolveIntensity(): number {
    const base = this.config.baseIntensity;
    const multiplier = this.deps.resolver.getTreePassiveEffect(
      'poison', 'dotDamageMultiplier', 'product',
    );
    return base * multiplier;
  }
}
